import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { API, fmtDT } from "@/lib/api";
import {
  PillBottle, ShoppingCart, Users, Newspaper, ShieldCheck, TrendingDown,
  ArrowRight, Boxes, MessagesSquare, Search, CheckCircle2,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1580281657527-47f249e8f4df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxwaGFybWFjaXN0JTIwd29ya2luZyUyMG1vZGVybnxlbnwwfHx8fDE3ODY4OTMwMzF8MA&ixlib=rb-4.1.0&q=85";
const WAREHOUSE = "https://images.unsplash.com/photo-1590247813693-5541d1c609fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxtZWRpY2FsJTIwd2FyZWhvdXNlJTIwYm94ZXN8ZW58MHx8fHwxNzg2ODkzMDMxfDA&ixlib=rb-4.1.0&q=85";

function Nav() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-indigo-600 grid place-items-center">
            <PillBottle className="h-5 w-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-extrabold text-slate-900">PharmaGroup</div>
            <div className="text-[10px] text-slate-400 tracking-wide">B2B TUNISIE</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors duration-200">Connexion</Link>
          <Link to="/register" data-testid="nav-register" className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200">Rejoindre</Link>
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const features = [
    { icon: ShoppingCart, title: "Achats groupés", desc: "Participez à des opérations négociées et bénéficiez de prix laboratoire imbattables." },
    { icon: TrendingDown, title: "Meilleures conditions", desc: "Mutualisez vos volumes avec d'autres pharmacies pour réduire vos coûts d'achat." },
    { icon: Search, title: "Recherche de produits", desc: "Trouvez rapidement un produit en rupture auprès du réseau d'adhérents et de fournisseurs." },
    { icon: MessagesSquare, title: "Réseau d'adhérents", desc: "Échangez des marchandises et communiquez avec les autres pharmaciens." },
    { icon: Newspaper, title: "Veille professionnelle", desc: "Circulaires, communiqués et alertes officielles centralisés en un seul endroit." },
    { icon: ShieldCheck, title: "Confidentialité", desc: "Vos données et commandes restent strictement privées et sécurisées." },
  ];

  const steps = [
    "La plateforme négocie un pack commercial avec le fournisseur",
    "L'opération est ouverte aux pharmacies adhérentes",
    "Chaque pharmacien compose sa participation produit par produit",
    "Les commandes sont centralisées puis transmises au fournisseur",
  ];

  return (
    <div className="bg-white">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold tracking-wide">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-600" /> PLATEFORME B2B POUR PHARMACIENS · TUNISIE
            </span>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Achetez <span className="text-indigo-600">groupé</span>,<br />payez moins cher.
            </h1>
            <p className="mt-6 text-lg font-medium text-slate-600 leading-relaxed max-w-lg">
              La première plateforme d'achats groupés de parapharmacie et compléments alimentaires
              qui met en relation pharmaciens et fournisseurs. Négociez collectivement, commandez simplement.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">
                Créer mon compte pharmacien <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-slate-300 hover:border-slate-400 text-slate-800 font-semibold transition-colors duration-200">
                Espace fournisseur
              </Link>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="absolute -inset-4 bg-indigo-100 rounded-[2rem] rotate-3" />
            <img src={HERO} alt="Pharmacien" className="relative rounded-[1.5rem] shadow-xl object-cover w-full h-[440px]" />
          </div>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="border-y border-slate-200 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { v: stats.pharmacists, l: "Pharmaciens adhérents" },
              { v: stats.suppliers, l: "Fournisseurs partenaires" },
              { v: stats.operations, l: "Opérations en cours" },
              { v: fmtDT(stats.total_committed), l: "Volume engagé" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-indigo-600 num">{s.v}</div>
                <div className="mt-1 text-sm font-medium text-slate-500">{s.l}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-2xl">
          Tout ce dont votre pharmacie a besoin, en une plateforme.
        </h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="p-6 rounded-xl border border-slate-200 hover:border-indigo-200 hover:-translate-y-0.5 transition-all duration-200 bg-white">
                <span className="h-11 w-11 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center"><Icon className="h-5.5 w-5.5" style={{ width: 22, height: 22 }} /></span>
                <h3 className="mt-4 text-lg font-bold text-slate-800">{f.title}</h3>
                <p className="mt-2 text-sm font-medium text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={WAREHOUSE} alt="Entrepôt médical" className="rounded-2xl object-cover w-full h-[380px]" />
          </div>
          <div>
            <span className="text-indigo-400 font-bold text-sm tracking-wide">LE MODÈLE D'ACHAT GROUPÉ</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">Comment ça marche ?</h2>
            <div className="mt-8 space-y-5">
              {steps.map((s, i) => (
                <div key={i} className="flex gap-4">
                  <span className="h-8 w-8 shrink-0 rounded-lg bg-indigo-600 grid place-items-center font-bold text-sm num">{i + 1}</span>
                  <p className="text-slate-300 font-medium pt-1">{s}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-2 text-emerald-400 text-sm font-semibold">
              <CheckCircle2 className="h-5 w-5" /> Le fournisseur livre et facture directement la pharmacie.
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center">
        <Boxes className="h-12 w-12 text-indigo-600 mx-auto" />
        <h2 className="mt-6 text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Rejoignez le réseau des pharmaciens</h2>
        <p className="mt-4 text-lg font-medium text-slate-500 max-w-xl mx-auto">Créez votre compte, votre inscription sera validée par notre équipe sous 24h.</p>
        <Link to="/register" className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">
          Commencer maintenant <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div className="flex items-center gap-2"><Users className="h-4 w-4" /> PharmaGroup B2B — Marché tunisien</div>
          <div>© 2026 · Confidentialité · Conditions</div>
        </div>
      </footer>
    </div>
  );
}
